import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useNombreJugador from '../hooks/useNombreJugador';
import './Player.css';
import {
  getJugadorReal,
  getJugadoresTemporadaReal,
  getRankingTotalJugadorReal,
  getOtrosResultadosReal,
  crearOtroResultadoReal,
  actualizarOtroResultadoReal,
  borrarOtroResultadoReal,
} from '../services/jugadoresService';
import { getRankingParticipantesReal } from '../services/rankingsService';
import { getParticipantesReal } from '../services/participantesService';
import { getCalendarioReal } from '../services/jornadasService';
import { useTemporada } from '../context/TemporadaContext';
import { useAuth } from '../context/AuthContext';

const FORM_RESULTADO_VACIO = { competicion: '', puntos: '' };
const MAX_JORNADAS = 14;
const JORNADAS = Array.from({ length: MAX_JORNADAS }, (_, i) => i + 1);

// Mismo orden que usan Ranking y Jornadas: así las tags de modalidad aparecen
// siempre en la misma posición para cualquier jugador, en vez del orden que
// devuelva la API (que varía según cómo se haya inscrito a cada uno).
const ORDEN_MODALIDADES = [
  'DOBLES FEMENINO', 'DOBLES MASCULINO', 'DOBLES MIXTO',
  'INDIVIDUAL FEMENINO', 'INDIVIDUAL MASCULINO',
];
const capitalizar = (s) => s.charAt(0) + s.slice(1).toLowerCase();
const ordenarModalidades = (modalidades) =>
  [...(modalidades ?? [])].sort((a, b) => ORDEN_MODALIDADES.indexOf(a) - ORDEN_MODALIDADES.indexOf(b));

const SIN_CLUB = 'Sin club registrado';
const agruparPorClub = (jugadores) => {
  const grupos = new Map();
  jugadores.forEach((j) => {
    const club = j.club ?? SIN_CLUB;
    if (!grupos.has(club)) grupos.set(club, []);
    grupos.get(club).push(j);
  });
  const clubes = [...grupos.keys()].sort((a, b) => {
    if (a === SIN_CLUB) return 1;
    if (b === SIN_CLUB) return -1;
    return a.localeCompare(b, 'es');
  });
  return clubes.map((club) => ({ club, jugadores: grupos.get(club) }));
};

function Player() {
  const location = useLocation();
  const nombreDe = useNombreJugador();
  const navigate = useNavigate();
  const playerId = new URLSearchParams(location.search).get('id');
  const jugadoresPath = location.pathname.startsWith('/dashboard') ? '/dashboard/jugadores' : '/jugadores';

  const [jugador, setJugador] = useState(null);
  const [rankingTotal, setRankingTotal] = useState(null);
  const [otrosResultados, setOtrosResultados] = useState([]);
  const [modalidadesTemporada, setModalidadesTemporada] = useState([]);
  const [clasificaciones, setClasificaciones] = useState([]);
  const [puntosPorJornada, setPuntosPorJornada] = useState({});
  const [lista, setLista] = useState(null);
  const [error, setError] = useState(null);
  const { temporada } = useTemporada();
  const { usuario } = useAuth();
  const esAdmin = usuario?.rol === 'ADMIN' || usuario?.rol === 'COMITE';

  const [formResultado, setFormResultado] = useState(FORM_RESULTADO_VACIO);
  const [editandoId, setEditandoId] = useState(null);
  const [guardandoResultado, setGuardandoResultado] = useState(false);
  const [errorResultado, setErrorResultado] = useState(null);

  const recargarOtrosResultados = useCallback(
    () => getOtrosResultadosReal(playerId, temporada).then(setOtrosResultados),
    [playerId, temporada]
  );

  useEffect(() => {
    if (!temporada) return;
    setError(null);

    if (playerId) {
      Promise.all([
        getJugadorReal(playerId),
        getRankingTotalJugadorReal(playerId),
        getOtrosResultadosReal(playerId, temporada),
        getParticipantesReal(temporada),
        getRankingParticipantesReal(temporada),
        getCalendarioReal(temporada),
      ])
        .then(([j, total, otros, participantes, rankingParticipantes, partidos]) => {
          setJugador(j);
          setRankingTotal(total);
          setOtrosResultados(otros);

          const misParticipantes = participantes.filter((p) =>
            p.jugadores.some((pj) => String(pj.jugadorId) === String(playerId))
          );
          const idsPropios = new Set(misParticipantes.map((p) => p.participante_id));

          setModalidadesTemporada(misParticipantes.map((p) => p.modalidad));
          setClasificaciones(
            rankingParticipantes
              .filter((r) => idsPropios.has(r.participante_id))
              .map((r) => ({
                ...r,
                totalModalidad: rankingParticipantes.filter((x) => x.modalidad === r.modalidad).length,
              }))
          );

          // Puntos de clasificación obtenidos en cada jornada (solo partidos
          // CONFIRMADOS cuentan), para dibujar las 14 columnas de jornada.
          const mapaJornadas = {};
          partidos.forEach((p) => {
            if (p.estado !== 'CONFIRMADO') return;
            if (idsPropios.has(p.local_participante_id)) {
              mapaJornadas[p.local_participante_id] ??= {};
              mapaJornadas[p.local_participante_id][p.jornada] = p.clasif_local;
            }
            if (idsPropios.has(p.visitante_participante_id)) {
              mapaJornadas[p.visitante_participante_id] ??= {};
              mapaJornadas[p.visitante_participante_id][p.jornada] = p.clasif_visitante;
            }
          });
          setPuntosPorJornada(mapaJornadas);
        })
        .catch(() => setError('No se pudo cargar el jugador. ¿Está el servidor en marcha?'));
    } else {
      getJugadoresTemporadaReal(temporada)
        .then(setLista)
        .catch(() => setError('No se pudo cargar la lista de jugadores. ¿Está el servidor en marcha?'));
    }
  }, [playerId, temporada]);

  const handleSubmitResultado = (e) => {
    e.preventDefault();
    setErrorResultado(null);

    const datos = {
      competicion: formResultado.competicion.trim(),
      temporadaId: temporada,
      puntos: Number(formResultado.puntos),
    };
    if (!datos.competicion || Number.isNaN(datos.puntos)) {
      setErrorResultado('Rellena competición y puntos.');
      return;
    }

    setGuardandoResultado(true);
    const guardar = editandoId
      ? actualizarOtroResultadoReal(editandoId, datos)
      : crearOtroResultadoReal(playerId, datos);

    guardar
      .then(() => {
        setFormResultado(FORM_RESULTADO_VACIO);
        setEditandoId(null);
        return recargarOtrosResultados();
      })
      .catch((err) => setErrorResultado(err.message || 'No se pudo guardar.'))
      .finally(() => setGuardandoResultado(false));
  };

  const handleEditarResultado = (r) => {
    setErrorResultado(null);
    setEditandoId(r.id);
    setFormResultado({ competicion: r.competicion, puntos: String(r.puntos) });
  };

  const handleCancelarEdicion = () => {
    setEditandoId(null);
    setFormResultado(FORM_RESULTADO_VACIO);
  };

  const handleBorrarResultado = (id) => {
    setErrorResultado(null);
    borrarOtroResultadoReal(id)
      .then(recargarOtrosResultados)
      .catch((err) => setErrorResultado(err.message || 'No se pudo borrar.'));
  };

  const gruposPorClub = useMemo(() => agruparPorClub(lista ?? []), [lista]);

  if (error) return <div className="player-page"><p className="msg-error">{error}</p></div>;

  if (!playerId) {
    return (
      <div className="player-page">
        <h2>Lista de Jugadores</h2>
        {gruposPorClub.map(({ club, jugadores }) => (
          <div key={club} className="club-group">
            <h3 className="club-heading">{club}</h3>
            <ul className="player-list">
              {jugadores.map((j) => (
                <li key={j.id} className="player-card">
                  <div className="player-item" onClick={() => navigate(`${jugadoresPath}?id=${j.id}`)}>
                    <div className="player-name">{nombreDe(j)}</div>
                    <div className="player-points">{j.puntos}</div>
                  </div>
                  {j.modalidades?.length > 0 && (
                    <div className="player-meta">
                      {ordenarModalidades(j.modalidades).map((m) => (
                        <span key={m} className="tag-modalidad">{capitalizar(m)}</span>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
        {lista && lista.length === 0 && <p className="ranking-empty">No hay jugadores inscritos en esta temporada.</p>}
      </div>
    );
  }

  return (
    <div className="player-page">
      <button type="button" className="player-volver" onClick={() => navigate(jugadoresPath)}>← Volver</button>
      <h2>Jugador</h2>
      {jugador && (
        <>
          <ul className="player-list">
            <li className="player-card">
              <div className="player-item selected">
                <div className="player-name">{nombreDe(jugador)}</div>
              </div>
              <div className="player-modalities">
                <p><strong>Club:</strong> {jugador.club ?? 'sin club registrado'}</p>
                <p><strong>Modalidades esta temporada:</strong></p>
                {modalidadesTemporada.length === 0 ? (
                  <p className="ranking-empty">No está inscrito en ninguna modalidad esta temporada.</p>
                ) : (
                  <ul>
                    {ordenarModalidades(modalidadesTemporada).map((m) => (
                      <li key={m} className="player-modalidad">{capitalizar(m)}</li>
                    ))}
                  </ul>
                )}
              </div>
            </li>
          </ul>

          <section className="player-section">
            <h3>Ranking total</h3>
            <div className="tabla-scroll">
              <table>
                <thead>
                  <tr><th>Temporada</th><th>J</th><th>G</th><th>Puntos</th></tr>
                </thead>
                <tbody>
                  {(rankingTotal?.porTemporada ?? []).map((t) => (
                    <tr key={`liga-${t.temporada_id}`}>
                      <td>Liga {t.temporada_id}</td>
                      <td>{t.jugados}</td>
                      <td>{t.ganados}</td>
                      <td className="bold">{t.puntos}</td>
                    </tr>
                  ))}
                  {(rankingTotal?.porTemporada ?? []).length === 0 && (
                    <tr><td colSpan={4}>Sin partidos de liga registrados.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="player-section">
            <h3>Otras competiciones ({temporada})</h3>
            <div className="tabla-scroll">
              <table>
                <thead>
                  <tr><th>Competición</th><th>Puntos</th>{esAdmin && <th></th>}</tr>
                </thead>
                <tbody>
                  {otrosResultados.map((r) => (
                    <tr key={r.id}>
                      <td>{r.competicion}</td>
                      <td className="bold">{r.puntos}</td>
                      {esAdmin && (
                        <td className="acciones-resultado">
                          <button type="button" onClick={() => handleEditarResultado(r)}>Editar</button>
                          <button type="button" onClick={() => handleBorrarResultado(r.id)}>Borrar</button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {otrosResultados.length === 0 && (
                    <tr><td colSpan={esAdmin ? 3 : 2}>Sin resultados esta temporada.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {esAdmin && (
              <form className="form-otro-resultado" onSubmit={handleSubmitResultado}>
                <input
                  placeholder="Competición (ej. COPA)"
                  value={formResultado.competicion}
                  onChange={(e) => setFormResultado({ ...formResultado, competicion: e.target.value })}
                />
                <input
                  type="number"
                  placeholder="Puntos"
                  value={formResultado.puntos}
                  onChange={(e) => setFormResultado({ ...formResultado, puntos: e.target.value })}
                />
                <button type="submit" disabled={guardandoResultado}>
                  {editandoId ? 'Guardar cambios' : 'Añadir'}
                </button>
                {editandoId && (
                  <button type="button" onClick={handleCancelarEdicion}>Cancelar</button>
                )}
              </form>
            )}
            {errorResultado && <p className="msg-error">{errorResultado}</p>}
          </section>

          <section className="player-section">
            <h3>Clasificación de esta temporada</h3>
            {clasificaciones.length === 0 && (
              <p className="ranking-empty">No compite en ninguna modalidad esta temporada.</p>
            )}
            {clasificaciones.map((c) => {
              const jornadasJugador = puntosPorJornada[c.participante_id] ?? {};
              return (
                <div key={c.participante_id} className="clasificacion-modalidad">
                  <h4>{c.modalidad}</h4>
                  <div className="tabla-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>Posición</th>
                          {JORNADAS.map((n) => <th key={n}>J{n}</th>)}
                          <th>J</th><th>G</th><th>P</th><th>Sets</th><th>Tantos</th><th>Puntos</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{c.posicion}º de {c.totalModalidad}</td>
                          {JORNADAS.map((n) => <td key={n}>{jornadasJugador[n] ?? ''}</td>)}
                          <td>{c.jugados}</td>
                          <td>{c.ganados}</td>
                          <td>{c.perdidos}</td>
                          <td>{c.sets_ganados}</td>
                          <td>{c.tantos_totales}</td>
                          <td className="bold">{c.puntos}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </section>
        </>
      )}
    </div>
  );
}

export default Player;
